import * as React from "react";
import { cn, Dialog, DialogContent, DialogDescription, DialogTitle } from "@aisquare/ui";
import { CornerDownLeftIcon, SearchIcon } from "lucide-react";
import { DEMOS, LAYERS, layerMetaFor, type DemoEntry } from "./registry";
import { hrefForComponent, navigate } from "./router";
import { Kbd, NewBadge } from "./chrome";

/**
 * ⌘K command palette.
 *
 * The cmdk / shadcn / Linear pattern every component library ships: ⌘K (or "/")
 * to open, type to filter, ↑/↓ to move, ↵ to open, Esc to close. Results are
 * real registry entries grouped by the library's own layers — no
 * "Blocks / Templates / Installation" buckets, because the playground has no
 * such concepts and inventing them would only mislead.
 *
 * Built on the library's own `<Dialog>` (Radix), so it dogfoods the design
 * system and inherits focus-trap + Esc handling with no extra dependency.
 */

interface PaletteItem extends DemoEntry {
  layerLabel: string;
}

/** Registry flattened once, with each layer's label folded in for matching. */
const ITEMS: PaletteItem[] = DEMOS.map((d) => ({
  ...d,
  layerLabel: layerMetaFor(d.layer).label,
}));

function itemMatches(item: PaletteItem, q: string): boolean {
  if (!q) return true;
  const hay = `${item.title} ${item.category} ${item.layerLabel} ${item.id}`.toLowerCase();
  return hay.includes(q);
}

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement>(null);

  const q = query.trim().toLowerCase();
  const results = React.useMemo(() => ITEMS.filter((item) => itemMatches(item, q)), [q]);

  // Group results by layer (in LAYERS order); each entry carries its flat
  // index into `results` so keyboard nav can address it directly.
  const groups = React.useMemo(() => {
    let idx = 0;
    return LAYERS.map((meta) => {
      const items = results
        .filter((r) => r.layer === meta.id)
        .map((item) => ({ item, index: idx++ }));
      return { meta, items };
    }).filter((group) => group.items.length > 0);
  }, [results]);

  // Reset the highlight to the top whenever the query or open-state changes.
  React.useEffect(() => {
    setActiveIndex(0);
  }, [q, open]);

  // Clear the query on close so the next open starts fresh.
  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const go = React.useCallback(
    (id: string) => {
      onOpenChange(false);
      navigate(hrefForComponent(id));
    },
    [onOpenChange],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = results[activeIndex];
      if (item) go(item.id);
    }
  };

  // Keep the highlighted row scrolled into view during keyboard nav.
  React.useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onKeyDown={onKeyDown}
        dir="ltr"
        className="top-[12vh] max-w-xl translate-y-0 gap-0 overflow-hidden p-0"
      >
        <DialogTitle className="sr-only">Search components</DialogTitle>
        <DialogDescription className="sr-only">
          Search every primitive in @aisquare/ui and open its page.
        </DialogDescription>

        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search components…"
            aria-label="Search components"
            className="h-12 w-full bg-transparent text-body text-foreground outline-none placeholder:text-muted-foreground"
          />
          <Kbd className="shrink-0">ESC</Kbd>
        </div>

        <div ref={listRef} className="max-h-[min(60vh,420px)] overflow-y-auto p-2">
          {groups.length === 0 ? (
            <div className="px-3 py-12 text-center text-body text-muted-foreground">
              No components match “{query}”.
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.meta.id} className="mb-2 last:mb-0">
                <div className="px-2 py-1.5 font-mono text-eyebrow uppercase text-muted-foreground/70">
                  {group.meta.label}
                </div>
                <ul>
                  {group.items.map(({ item, index }) => {
                    const active = index === activeIndex;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          data-index={index}
                          onMouseMove={() => setActiveIndex((i) => (i === index ? i : index))}
                          onClick={() => go(item.id)}
                          className={cn(
                            "flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-start text-label transition-colors",
                            active ? "bg-accent/10 text-accent" : "text-foreground hover:bg-muted",
                          )}
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="truncate font-medium">{item.title}</span>
                            <span className="truncate text-caption text-muted-foreground">
                              {item.category}
                            </span>
                            <NewBadge since={item.since} />
                          </span>
                          {active ? (
                            <CornerDownLeftIcon className="size-3.5 shrink-0" />
                          ) : (
                            <span className="shrink-0 font-mono text-[10px] text-muted-foreground/60">
                              {item.id}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-4 border-t border-border px-4 py-2.5 font-mono text-caption text-muted-foreground">
          <span className="flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <Kbd>↵</Kbd>
            open
          </span>
          <span className="ms-auto tabular-nums">
            {results.length} {results.length === 1 ? "result" : "results"}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
